import React, { useContext, useEffect, useState } from "react";
import ProfilePicture from "../../resources/icons/profile-circle-disabled.svg";
import AuthContext from "../../store/auth-context";
import { POST, GET } from "../../service/CRUDService";
import EntityConstants from "../../configs/EntityConstants";
import toast from "react-hot-toast";
import EmailIcon from "../../resources/icons/icons8-email-48_purple.svg";
import CfLoader from "../../common/CfLoader/CfLoader";
import { useTranslation } from "react-i18next";

const Profile = () => {
  const authContext = useContext(AuthContext);
  const { t } = useTranslation();

  const [isFieldChanged, setIsFieldChanged] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [initialData, setInitialData] = useState({});
  const [phoneError, setPhoneError] = useState("");
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    gender: `${t("profile:male")}`,
    email: "",
    date_of_birth: "",
    phone: "",
    profile_photo: null,
  });

  const [previewSrc, setPreviewSrc] = useState(ProfilePicture);

  const validatePhoneNumber = (phone) => {
    if (!phone) {
      return { isValid: false, error: `${t("profile:phone_required")}` };
    }

    const phoneRegex = /^(\+91[\s-]?|91[\s-]?|0)?[6-9]\d{9}$/;
    const isValid = phoneRegex.test(phone);

    return {
      isValid,
      error: isValid ? "" : `${t("profile:phone_invalid_example")}`,
    };
  };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setIsLoading(true);
        const response = await GET(EntityConstants.CURRENT_USER);
        if (response.ok) {
          const userData = await response.json();

          const userFormData = {
            first_name: userData.first_name || "",
            last_name: userData.last_name || "",
            gender: userData.gender || `${t("profile:male")}`,
            email: userData.email || "",
            date_of_birth: userData.date_of_birth || "",
            phone: userData.phone || "",
            profile_photo: userData.profile_photo || null,
          };

          setInitialData(userFormData);
          setFormData(userFormData);

          if (!userFormData.phone) {
            setPhoneError(t("profile:phone_required"));
          }

          if (userData.profile_photo) {
            setPreviewSrc(userData.profile_photo);
          }

          if (
            authContext.userDetails &&
            Object.keys(authContext.userDetails).length === 0
          ) {
            authContext.createUserProfileDetails(userData);
          }
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
        toast.error(t("profile:failed_load_profile"));
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, [authContext]);

  useEffect(() => {
    const hasChanges = Object.keys(formData).some((key) => {
      if (key === "profile_photo") {
        return (
          formData[key] !== initialData[key] &&
          formData[key] !== null &&
          formData[key] !== initialData.profile_photo
        );
      }
      return formData[key] !== initialData[key];
    });

    setIsFieldChanged(hasChanges);
  }, [formData, initialData]);

  const handleChange = (event) => {
    const { name, value, type, files } = event.target;

    if (type === "file") {
      const file = files[0];
      if (file) {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onloadend = () => {
          const base64String = reader.result;
          setFormData((prevData) => ({
            ...prevData,
            [name]: base64String,
          }));
          setPreviewSrc(base64String);
        };
      }
    } else {
      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
      }));

      if (name === "phone") {
        const validation = validatePhoneNumber(value);
        setPhoneError(validation.error);
      }
    }
  };

  const handlePhoneInput = (event) => {
    const { value } = event.target;

    let formattedValue = value.replace(/[^\d+]/g, "");

    if (
      formattedValue.startsWith("+") &&
      formattedValue.length > 3 &&
      formattedValue.indexOf(" ") === -1
    ) {
      formattedValue =
        formattedValue.substring(0, 3) + " " + formattedValue.substring(3);
    }

    setFormData((prevData) => ({
      ...prevData,
      phone: formattedValue,
    }));

    const validation = validatePhoneNumber(formattedValue);
    setPhoneError(validation.error);
  };

  const isFormValid = () => {
    const phoneValidation = validatePhoneNumber(formData.phone);
    return isFieldChanged && phoneValidation.isValid;
  };

  const handleSave = async () => {
    const phoneValidation = validatePhoneNumber(formData.phone);
    if (!phoneValidation.isValid) {
      toast.error(phoneValidation.error);
      return;
    }

    const payload = {};

    Object.keys(formData).forEach((key) => {
      if (key === "profile_photo") {
        if (
          formData[key] !== initialData[key] &&
          formData[key] !== null &&
          formData[key] !== initialData.profile_photo
        ) {
          payload[key] = formData[key];
        }
      } else if (formData[key] !== initialData[key]) {
        payload[key] = formData[key];
      }
    });

    if (Object.keys(payload).length === 0) {
      toast(`${t("profile:no_changes_save")}`, { icon: "ℹ️" });
      return;
    }

    try {
      const response = await POST(
        `${EntityConstants.CURRENT_USER}/update`,
        payload
      );
      const result = await response.json();

      if (response.ok) {
        toast.success(t("profile:profile_update_success"));
        setInitialData(formData);
        setIsFieldChanged(false);
        setPhoneError("");
      } else {
        if (result.error) {
          let errorMessage = t("profile:failed_update_profile");

          if (result.error.phone) {
            errorMessage = `Phone: ${result.error.phone.join(", ")}`;
            setPhoneError(result.error.phone.join(", "));
          } else if (result.message) {
            errorMessage = result.message;
          }

          toast.error(errorMessage);
        } else {
          toast.error(t("profile:failed_update_profile"));
        }
      }
    } catch (error) {
      console.log("Error:", error);
      toast.error(t("profile:failed_update_profile"));
    }
  };

  function ProfilePicUpload({
    id,
    altText,
    onFileUpload,
    logoHeading,
    logoSubHeading,
  }) {
    const [dragActive, setDragActive] = useState(false);

    const handleFileChange = (event) => {
      const file = event.target.files[0];
      processFile(file);
    };

    const handleDragOver = (event) => {
      event.preventDefault();
      event.stopPropagation();
      setDragActive(true);
    };

    const handleDragLeave = (event) => {
      event.preventDefault();
      event.stopPropagation();
      setDragActive(false);
    };

    const handleDrop = (event) => {
      event.preventDefault();
      event.stopPropagation();
      setDragActive(false);
      const file = event.dataTransfer.files[0];
      processFile(file);
    };

    const handleKeyDown = (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        document.getElementById(id).click();
      }
    };

    const processFile = (file) => {
      const allowedFileTypes = ["png", "jpeg", "jpg"];
      const allowedMimeTypes = ["image/png", "image/jpeg", "image/jpg"];

      if (file) {
        const extension = file.name.split(".").pop().toLowerCase();
        const isValidType = allowedFileTypes.includes(extension);
        const isValidMime = allowedMimeTypes.includes(file.type);

        if (isValidType && isValidMime) {
          const maxSize = 5 * 1024 * 1024;
          if (file.size > maxSize) {
            toast.error(t("profile:file_size_error_5mb"));
            return;
          }
          onFileUpload(file);
        } else {
          toast.error(t("profile:image_format_error"));
        }
      } else {
        toast.error(t("profile:select_image_error"));
      }
    };

    return (
      <div
        id="profile-photo-dropzone"
        className={`flex gap-4 py-6 px-4 mt-2 cursor-pointer text-gray-500 bg-white rounded-lg outline-dotted outline-gray-300 outline-offset-2 shadow-sm ${
          dragActive ? "bg-gray-100" : ""
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
      >
        <input
          id={id}
          type="file"
          accept=".png,.jpeg,.jpg,image/png,image/jpeg,image/jpg,image"
          onChange={handleFileChange}
          className="hidden"
          loading="lazy"
        />
        <label id="profile-photo-upload-label" htmlFor={id} className="cursor-pointer flex flex-row w-full">
          <img
            loading="lazy"
            src="https://cdn.builder.io/api/v1/image/assets/TEMP/e6c376ce7ba432a6c2a0781570893c28440a280d4825eeedee07208e78e63d79?apiKey=1ca7de1f58f745cd95efc0640d2ef737&"
            alt={altText}
            className="shrink-0 my-auto w-9 mr-4 aspect-square"
          />
          <div className="flex flex-col">
            <div className="text-sm">
              <span className="font-semibold text-cfPrimary">
                {logoHeading} {logoSubHeading}
              </span>
            </div>
            <div className="mt-2 text-xs">{t("profile:image_upload_hint")}</div>
          </div>
        </label>
      </div>
    );
  }

  const getSaveButtonTitle = () => {
    if (!isFieldChanged) return t("profile:make_changes_to_save");
    if (phoneError) return t("profile:fix_phone_to_save");
    if (!formData.phone) return t("profile:phone_required");
    return t("profile:save");
  };

  if (isLoading) {
    return (
      <div className="w-full flex justify-center items-center">
        <CfLoader />
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center w-full mt-20 py-12 px-5 bg-cfSecondary min-h-screen">
      <div className="text-xl font-semibold text-gray-900 w-full mb-4 flex justify-between items-center">
        <h1>{t("profile:profile")}</h1>
        <button
          id="profile-save-button"
          disabled={!isFormValid()}
          onClick={handleSave}
          title={getSaveButtonTitle()}
          className="px-4 py-2 rounded-lg bg-cfPrimary hover:bg-cfPrimaryDark text-base text-white disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          {t("profile:save")}
        </button>
      </div>
      <div className="border rounded-xl w-full relative">
        <div className="px-4 pb-4">
          <div className="flex gap-4 items-center mb-4 justify-between py-4">
            <div className="flex items-center gap-8 justify-center">
              <img
                src={previewSrc}
                className="w-24 h-24 rounded-full"
                alt="Profile"
              />
            </div>
            <ProfilePicUpload
              id="profile-photo-file-input"
              altText="Profile Upload"
              logoHeading={
                formData.profile_photo
                  ? t("profile:change_profile_photo")
                  : t("profile:upload_profile_photo")
              }
              logoSubHeading=""
              onFileUpload={(file) => {
                const reader = new FileReader();
                reader.readAsDataURL(file);
                reader.onloadend = () => {
                  const base64String = reader.result;
                  setFormData((prevData) => ({
                    ...prevData,
                    profile_photo: base64String,
                  }));
                  setPreviewSrc(base64String);
                };
              }}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <span className="max-w-lg">
              <label
                htmlFor="profile-first-name-input"
                className="text-sm font-semibold text-black"
              >
                {t("profile:first_name")}
              </label>
              <input
                type="text"
                placeholder={t("profile:first_name")}
                className="border rounded-lg p-2 w-full text-sm placeholder:text-sm"
                name="first_name"
                id="profile-first-name-input"
                value={formData.first_name}
                onChange={handleChange}
              />
            </span>

            <span className="max-w-lg">
              <label
                htmlFor="profile-last-name-input"
                className="text-sm font-semibold text-black"
              >
                {t("profile:last_name")}
              </label>
              <input
                type="text"
                placeholder={t("profile:last_name")}
                className="border rounded-lg p-2 w-full text-sm placeholder:text-sm"
                name="last_name"
                id="profile-last-name-input"
                value={formData.last_name}
                onChange={handleChange}
              />
            </span>
            <span className="max-w-lg">
              <label
                htmlFor="profile-gender-select"
                className="text-sm font-semibold text-black"
              >
                {t("profile:gender")}
              </label>
              <select
                name="gender"
                id="profile-gender-select"
                value={formData.gender}
                onChange={handleChange}
                className="border rounded-lg p-2 w-full text-sm placeholder:text-sm"
              >
                <option value="Male">{t("profile:male")}</option>
                <option value="Female">{t("profile:female")}</option>
              </select>
            </span>
            <span className="max-w-lg">
              <label
                htmlFor="profile-phone-input"
                className="text-sm font-semibold text-black"
              >
                {t("profile:mobile")} <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                placeholder="+91 9999999999"
                className={`border rounded-lg p-2 w-full text-sm placeholder:text-sm ${
                  phoneError ? "border-red-500" : ""
                }`}
                name="phone"
                id="profile-phone-input"
                value={formData.phone}
                onChange={handleChange}
                onInput={handlePhoneInput}
                required
              />
              {phoneError && (
                <p className="text-red-500 text-xs mt-1">{phoneError}</p>
              )}
            </span>
          </div>
        </div>
      </div>
      <div className="self-start mt-4">
        <span className="flex items-center gap-2 border rounded-2xl text-sm p-3 ">
          <span className="w-[5.5rem] h-[5.5rem] rounded-full bg-[#4338CA] flex items-center justify-center">
            <img src={EmailIcon} alt="" className="w-[5.5rem] h-[5.5rem]" />
          </span>
          <span className="text-black text-sm">
            <p id="profile-name-display">
              {formData.first_name + " " + formData.last_name}
            </p>
            <p id="profile-email-display">{formData.email}</p>
          </span>
        </span>
      </div>
    </div>
  );
};

export default Profile;
